import React, { useState } from 'react';
import { formatInTenantTz } from '../../lib/datetime';
import { useAuth } from '../../contexts/AuthContext';
import { useBusiness } from '../../contexts/BusinessContext';
import { useCRMAuth } from '../../contexts/CRMAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Plus, 
  Trash2, 
  Edit, 
  Save,
  Eye,
  DollarSign,
  Clock,
  Users,
  AlertCircle,
  CheckCircle,
  Gift
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import SurveyManager from './SurveyManager';   // BUS-6/7: edit + results/download for existing surveys

const CreateSurvey = () => {
  const { user } = useAuth();
  const { currentBusiness } = useBusiness();
  const { makeAuthenticatedRequest } = useCRMAuth();
  const { toast } = useToast();
  const { t } = useLanguage();

  const [survey, setSurvey] = useState({
    title: '',
    description: '',
    survey_type: 'paid',
    reward: '',
    duration: '',
    difficulty: 'Easy',
    deadline: '',
    questions: []
  });
  
  const [loading, setLoading] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState({
    type: 'multiple_choice',
    question: '',
    options: ['', ''],
    required: true
  });

  const questionTypes = [
    { value: 'multiple_choice', label: t('pg.survey.multipleChoice') },
    { value: 'text', label: t('pg.survey.textResponse') },
    { value: 'rating', label: t('pg.survey.ratingScale') },
    { value: 'yes_no', label: t('pg.survey.yesNo') }
  ];

  const addQuestion = () => {
    if (!currentQuestion.question.trim()) {
      toast({
        title: t('common.error'),
        description: t('pg.survey.enterQuestion'),
        variant: "destructive",
      });
      return;
    }

    const newQuestion = {
      ...currentQuestion,
      id: Date.now().toString(),
      options: currentQuestion.type === 'multiple_choice' ? 
        currentQuestion.options.filter(opt => opt.trim()) : 
        currentQuestion.options
    };

    setSurvey(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion]
    }));

    setCurrentQuestion({
      type: 'multiple_choice',
      question: '',
      options: ['', ''],
      required: true
    });

    toast({
      title: t('pg.survey.questionAdded'),
      description: t('pg.survey.questionAddedDesc'),
    });
  };

  const removeQuestion = (questionId) => {
    setSurvey(prev => ({
      ...prev,
      questions: prev.questions.filter(q => q.id !== questionId)
    }));
  };

  const addOption = () => {
    setCurrentQuestion(prev => ({
      ...prev,
      options: [...prev.options, '']
    }));
  };

  const updateOption = (index, value) => {
    setCurrentQuestion(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => i === index ? value : opt)
    }));
  };

  const removeOption = (index) => {
    if (currentQuestion.options.length > 2) {
      setCurrentQuestion(prev => ({
        ...prev,
        options: prev.options.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!survey.title || !survey.description || survey.questions.length === 0) {
      toast({
        title: t('pg.survey.missingInfo'),
        description: t('pg.survey.missingInfoDesc'),
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // MGR-09: the tenant is `currentBusiness.brandId`. This read `currentBusiness?.id`, a field the
      // business object has never carried (BusinessContext builds brandId/businessName/…, no `id`),
      // so it was ALWAYS undefined and the payload fell back to the literal string
      // "default-business". The backend then rejected it at
      // `assert_tenant_member(caller, body.business_id)` — a correct cross-tenant refusal of a tenant
      // that does not exist. Runtime-confirmed: POST /api/v1/surveys -> 403, while PUT on an existing
      // survey succeeded for the same Manager because the update path takes the tenant from the
      // STORED document instead of the request body.
      //
      // `businessName` had the same bug via `currentBusiness?.name` ("Default Business").
      //
      // No literal fallback: a missing tenant must fail closed here rather than post a fabricated
      // business_id that the server has to refuse.
      const brandId = currentBusiness?.brandId;
      if (!brandId) {
        toast({ title: t('common.error') || 'Error',
                description: 'No active business selected.', variant: 'destructive' });
        setLoading(false);
        return;
      }
      const surveyData = {
        ...survey,
        business_id: brandId,
        business_name: currentBusiness?.businessName || '',
        creator_id: user.id,
        deadline: new Date(survey.deadline).toISOString()
      };

      const response = await makeAuthenticatedRequest('/api/v1/surveys', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(surveyData),
      });

      if (response.ok) {
        toast({
          title: t('pg.survey.surveyCreated'),
          description: t('pg.survey.surveyCreatedDesc'),
        });
        
        // Reset form
        setSurvey({
          title: '',
          description: '',
          survey_type: 'paid',
          reward: '',
          duration: '',
          difficulty: 'Easy',
          deadline: '',
          questions: []
        });
      } else {
        throw new Error('Failed to create survey');
      }
    } catch (error) {
      console.error('Error creating survey:', error);
      toast({
        title: t('common.error'),
        description: t('pg.survey.createFailed'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 h-full overflow-y-auto p-6 bg-gradient-to-br from-white via-green-50 to-purple-50">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('pg.survey.createSurvey')}</h1>
        <p className="text-gray-600">{t('pg.survey.subtitle')}</p>
      </div>

      <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-8">
        {/* Main Survey Form */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Edit className="h-5 w-5" />
                <span>{t('pca.survey.surveyDetails')}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <form onSubmit={handleSubmit}>
                {/* Basic Information */}
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="title">{t('pca.survey.surveyTitleReq')}</Label>
                    <Input
                      id="title"
                      placeholder={t('pca.survey.enterTitle')}
                      value={survey.title}
                      onChange={(e) => setSurvey(prev => ({ ...prev, title: e.target.value }))}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="description">{t('pca.survey.descriptionReq')}</Label>
                    <Textarea
                      id="description"
                      placeholder={t('pca.survey.describeSurvey')}
                      value={survey.description}
                      onChange={(e) => setSurvey(prev => ({ ...prev, description: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="survey_type">{t('pca.survey.surveyTypeReq')}</Label>
                      <Select 
                        value={survey.survey_type} 
                        onValueChange={(value) => setSurvey(prev => ({ ...prev, survey_type: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="paid">
                            <div className="flex items-center space-x-2">
                              <DollarSign className="h-4 w-4 text-green-600" />
                              <span>{t('pca.survey.paidSurvey')}</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="unpaid">
                            <div className="flex items-center space-x-2">
                              <Gift className="h-4 w-4 text-blue-600" />
                              <span>{t('pca.survey.unpaidSurvey')}</span>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="reward">{t('pca.survey.reward')}</Label>
                      <Input
                        id="reward"
                        placeholder={survey.survey_type === 'paid' ? '$15' : t('pca.survey.communityPoints')}
                        value={survey.reward}
                        onChange={(e) => setSurvey(prev => ({ ...prev, reward: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="duration" className="flex items-center space-x-1">
                        <Clock className="h-3.5 w-3.5 text-gray-500" />
                        <span>{t('pca.survey.duration')}</span>
                      </Label>
                      <Input
                        id="duration"
                        placeholder={t('pca.survey.durationExample')}
                        value={survey.duration}
                        onChange={(e) => setSurvey(prev => ({ ...prev, duration: e.target.value }))}
                      />
                    </div>

                    <div>
                      <Label htmlFor="difficulty">{t('pca.survey.difficulty')}</Label>
                      <Select 
                        value={survey.difficulty} 
                        onValueChange={(value) => setSurvey(prev => ({ ...prev, difficulty: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Easy">{t('pca.survey.easy')}</SelectItem>
                          <SelectItem value="Medium">{t('pca.survey.medium')}</SelectItem>
                          <SelectItem value="Advanced">{t('pca.survey.advanced')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="deadline">{t('pca.survey.deadlineReq')}</Label>
                      <Input
                        id="deadline"
                        type="date"
                        value={survey.deadline}
                        onChange={(e) => setSurvey(prev => ({ ...prev, deadline: e.target.value }))}
                        required
                      />
                    </div>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Question Builder */}
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Plus className="h-5 w-5" />
                <span>{t('pca.survey.addQuestions')}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="question_type">{t('pca.survey.questionType')}</Label>
                <Select 
                  value={currentQuestion.type} 
                  onValueChange={(value) => setCurrentQuestion(prev => ({ 
                    ...prev, 
                    type: value,
                    options: value === 'multiple_choice' ? ['', ''] : []
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {questionTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="question_text">{t('pca.survey.questionReq')}</Label>
                <Textarea
                  id="question_text"
                  placeholder={t('pca.survey.enterYourQuestion')}
                  value={currentQuestion.question}
                  onChange={(e) => setCurrentQuestion(prev => ({ ...prev, question: e.target.value }))}
                />
              </div>

              {currentQuestion.type === 'multiple_choice' && (
                <div>
                  <Label>{t('pca.survey.options')}</Label>
                  <div className="space-y-2">
                    {currentQuestion.options.map((option, index) => (
                      <div key={index} className="flex space-x-2">
                        <Input
                          placeholder={t('pca.survey.optionN', { n: index + 1 })}
                          value={option}
                          onChange={(e) => updateOption(index, e.target.value)}
                        />
                        {currentQuestion.options.length > 2 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removeOption(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addOption}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      {t('pca.survey.addOption')}
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={currentQuestion.required}
                  onChange={(e) => setCurrentQuestion(prev => ({ ...prev, required: e.target.checked }))}
                />
                <Label>{t('pca.survey.requiredQuestion')}</Label>
              </div>

              <Button
                type="button"
                onClick={addQuestion}
                className="w-full bg-gradient-to-r from-green-500 to-purple-600 hover:from-green-600 hover:to-purple-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('pca.survey.addQuestion')}
              </Button>
            </CardContent>
          </Card>

          {/* Questions List */}
          {survey.questions.length > 0 && (
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span>{t('pca.survey.questionsCount', { count: survey.questions.length })}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {survey.questions.map((question, index) => (
                    <div key={question.id} className="p-4 border rounded-lg bg-white/50">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <Badge variant="outline">{question.type.replace('_', ' ')}</Badge>
                            {question.required && (
                              <Badge variant="secondary" className="flex items-center space-x-1">
                                <AlertCircle className="h-3 w-3" />
                                <span>{t('pca.survey.required')}</span>
                              </Badge>
                            )}
                          </div>
                          <h4 className="font-medium">{index + 1}. {question.question}</h4>
                          {question.options && question.options.length > 0 && (
                            <ul className="mt-2 text-sm text-gray-600">
                              {question.options.map((option, i) => (
                                <li key={i}>• {option}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeQuestion(question.id)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full bg-gradient-to-r from-green-500 to-purple-600 hover:from-green-600 hover:to-purple-700 disabled:opacity-50"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {t('pca.survey.creatingSurvey')}
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {t('pg.survey.createSurvey')}
              </>
            )}
          </Button>
        </div>

        {/* Preview Panel */}
        <div className="space-y-6">
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Eye className="h-5 w-5" />
                <span>{t('pca.survey.preview')}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="details">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="details">{t('pca.survey.details')}</TabsTrigger>
                  <TabsTrigger value="audience">{t('pca.survey.audience')}</TabsTrigger>
                </TabsList>
                <TabsContent value="details">
                  {survey.title ? (
                    <div className="space-y-4 mt-4">
                      <div>
                        <h3 className="font-semibold text-lg">{survey.title}</h3>
                        <p className="text-sm text-gray-600">{survey.description}</p>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span>{t('pca.survey.typeLabel')}</span>
                          <Badge className={survey.survey_type === 'paid' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}>
                            {survey.survey_type === 'paid' ? t('pca.survey.paid') : t('pca.survey.unpaid')}
                          </Badge>
                        </div>
                        {survey.reward && (
                          <div className="flex items-center justify-between text-sm">
                            <span>{t('pca.survey.rewardLabel')}</span>
                            <span className="font-medium">{survey.reward}</span>
                          </div>
                        )}
                        {survey.duration && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="flex items-center space-x-1">
                              <Clock className="h-3.5 w-3.5 text-gray-500" />
                              <span>{t('pca.survey.durationLabel')}</span>
                            </span>
                            <span className="font-medium">{survey.duration}</span>
                          </div>
                        )}
                        <div className="flex items-center justify-between text-sm">
                          <span>{t('pca.survey.difficultyLabel')}</span>
                          <Badge variant="outline">{survey.difficulty}</Badge>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span>{t('pca.survey.questionsLabel')}</span>
                          <span className="font-medium">{survey.questions.length}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                        <Eye className="h-6 w-6 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-500">{t('pca.survey.fillToPreview')}</p>
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="audience">
                  <div className="space-y-4 mt-4">
                    <div className="flex items-center space-x-2 text-sm text-gray-700">
                      <Users className="h-4 w-4 text-purple-600" />
                      <span>{t('pca.survey.targetAll', { business: currentBusiness?.businessName || t('pca.survey.yourBusiness') })}</span>
                    </div>
                    {survey.deadline && (
                      <div className="flex items-center space-x-2 text-sm text-gray-700">
                        <Clock className="h-4 w-4 text-orange-500" />
                        <span>{t('pca.survey.deadlineLabel')} {formatInTenantTz(survey.deadline, { dateStyle: 'medium' })}</span>
                      </div>
                    )}
                    <div className="flex items-center space-x-2 text-sm text-gray-700">
                      <AlertCircle className="h-4 w-4 text-yellow-500" />
                      <span>{t('pca.survey.requiredQuestions', { count: survey.questions.filter(q => q.required).length })}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-700">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>{t('pca.survey.totalQuestions', { count: survey.questions.length })}</span>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Tips */}
          <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
            <CardHeader>
              <CardTitle className="text-lg text-blue-900">{t('pca.survey.surveyTips')}</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-blue-800">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                  <span>{t('pca.survey.tip1')}</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                  <span>{t('pca.survey.tip2')}</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                  <span>{t('pca.survey.tip3')}</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                  <span>{t('pca.survey.tip4')}</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                  <span>{t('pca.survey.tip5')}</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* BUS-6/7: manage existing surveys — edit + view/download results */}
      <div className="max-w-6xl mx-auto">
        <SurveyManager />
      </div>
    </div>
  );
};

export default CreateSurvey;